import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import dayjs from 'dayjs';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilterList } from '@fortawesome/pro-solid-svg-icons';

import CHARTCOLORS from '../../../../helpers/chartColors';
import AccountSelectModal from '../../../../components/modals/AccountSelectModal';
import EnergyTracerMultiLineChart from '../../../../components/EnergyTracerMultiLineChart';
import GraphActionContainer from '../../../../components/chartElements/GraphActionContainer';
import ChartToggle from '../../../../components/chartElements/Actions/ChartToggle';
import ChartDownload from '../../../../components/chartElements/Actions/ChartDownload';
import DashboardDateSelectContainer from '../../../Dashboard/DashboardDateSelectContainer';
import MONTHSABBREVIATED from '../../../../helpers/monthsAbbreviated';
import AccountChartingBaselineContainer from './AccountChartingBaselineSelect';
import consumptionUnits, { UNIT_UTILITIES, ET_FRONTEND_CONVERSIONS } from '../../../../helpers/consumptionUnits';
import Loader from '../../../../components/Loader';
import { consumptionKeys, costKeys } from '../../helpers';
import ChartInfoPopover from '../../../../components/chartElements/Actions/ChartInfoPopover';
import { addAccountTypes } from '../../../../helpers/utility-types';

const styles = {
  inputStyle: {
    width: '200px',
    textAlign: 'center',
    marginRight: '10px',
  },
  unit_form: { display: 'inline-block', marginLeft: '1em' },
};

const row = {
  marginRight: '40px',
  paddingLeft: '1em',
  marginTop: '1em',
};

const activeUtilities = [...addAccountTypes]


export default function AccountCharting(props) {
  const { organizationId, rawBills, accountType, isLoadingBills } = props;

  const [showAccountModal, setShowAccountModal] = useState(false);

  const [utilitySelected, setUtilitySelected] = useState('electric');
  const [unitSelected, setUnitSelected] = useState('kWh');

  const [subAccountInfo, setSubAccountInfo] = useState({});
  const [subAccountIds, setSubAccountIds] = useState([]);
  const [activeIds, setActiveIds] = useState([]);
  const [activeKeys, setActiveKeys] = useState([]);

  const [formattedBills, setFormattedBills] = useState({});

  const [dateOption, setDateOption] = useState(1);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const [baselineStartDate, setBaselineStartDate] = useState(null);
  const [baselineEndDate, setBaselineEndDate] = useState(null);

  const [baseline2StartDate, setBaseline2StartDate] = useState(null);
  const [baseline2EndDate, setBaseline2EndDate] = useState(null);

  const [costData, setCostData] = useState([]);
  const [usageData, setUsageData] = useState([]);

  const [costSummary, setCostSummary] = useState(true);
  const [usageSummary, setUsageSummary] = useState(true);

  const [chartDownloadHeaders, setChartDownloadHeaders] = useState([]);
  const [chartDownloadKeys, setChartDownloadKeys] = useState([]);

  const [consumptionKey, setConsumptionKey] = useState(0)
  const [costKey, setCostKey] = useState(0)


  useEffect(() => {
    if (UNIT_UTILITIES.includes(utilitySelected)) {
      setUnitSelected(consumptionUnits[utilitySelected][0]);
    } else if (utilitySelected === 'electric') {
      setUnitSelected('kwh');
    } else {
      setUnitSelected('');
    }
  }, [utilitySelected]);


  useEffect(() => {
    let end_date = dayjs.utc();
    let start_date = end_date;
    start_date = start_date.month(start_date.month() - 11).startOf('month');

    setEndDate(end_date);
    setStartDate(start_date);
  }, [organizationId]);

  useEffect(() => {
    setUtilitySelected(accountType);
  }, [accountType]);

  useEffect(() => {
    let headers = ['Date'];
    let keys = ['name'];
    activeKeys.forEach((key) => {
      headers.push(key);
      keys.push(key);
      if (baselineStartDate) {
        headers.push(key + ' Baseline');
        keys.push(key + ' Baseline');
      }
      if (baseline2StartDate) {
        headers.push(key + ' Baseline 2');
        keys.push(key + ' Baseline 2');
      }
    });
    setChartDownloadHeaders(headers);
    setChartDownloadKeys(keys);
  }, [activeKeys, baselineStartDate, baseline2StartDate]);

  useEffect(() => {
    setSubAccountIds([]);
    setActiveIds([]);
    setActiveKeys([]);
    setSubAccountInfo({});
    setCostData([]);
    setUsageData([]);

    let accounts = {};
    rawBills.forEach((bill) => {
      if (bill.bill_type === utilitySelected) {
        accounts[bill.provider_meter_id] = bill.provider_meter_name
          ? bill.provider_meter_name
          : 'Manual';
      }
    });

    let account_ids = Object.keys(accounts);
    let account_name = Object.values(accounts);
    let account_info = {};
    setSubAccountIds(account_ids);
    setActiveIds(account_ids);
    setActiveKeys(account_name);
    account_ids.forEach((id) => {
      account_info[id] = { id: id, name: accounts[id] };
    });
    setSubAccountInfo(account_info);
  }, [rawBills, utilitySelected]);



  // format data
  useEffect(() => {
    const consumption_key = consumptionKeys[consumptionKey]['key']
    const cost_key = costKeys[costKey]['key']

    let bills_formatted = {};
    rawBills.forEach((bill) => {
      const bill_type = bill.bill_type;

      let date_key = 'bill_date';
      if (parseInt(dateOption) === 2) {
        date_key = 'read_date';
      }
      const bill_month = parseInt(bill[date_key].substring(5, 7));
      const bill_year = bill[date_key].substring(0, 4);
      const bill_account = bill.provider_meter_name
        ? bill.provider_meter_name
        : 'Manual';
      if (!(bill_type in bills_formatted)) {
        bills_formatted[bill_type] = {};
      }
      if (!(bill_year in bills_formatted[bill_type])) {
        bills_formatted[bill_type][bill_year] = {};
      }
      if (!(bill_month in bills_formatted[bill_type][bill_year])) {
        bills_formatted[bill_type][bill_year][bill_month] = {};
        const month_str =
          MONTHSABBREVIATED[bill_month - 1] + '-' + bill_year.substring(2, 4);
        bills_formatted[bill_type][bill_year][bill_month]['name'] = month_str;
        bills_formatted[bill_type][bill_year][bill_month]['month'] = bill_year;
        bills_formatted[bill_type][bill_year][bill_month]['cost'] = {};
        bills_formatted[bill_type][bill_year][bill_month]['usage'] = {};
      }
      const usage = consumption_key === 'total_consumption' ? bill[consumption_key] : bill['excluded_defaults'][consumption_key]
      if (
        bill_account in
        bills_formatted[bill_type][bill_year][bill_month]['usage']
      ) {
        bills_formatted[bill_type][bill_year][bill_month]['usage'][
          bill_account
        ] += usage;
      } else {
        bills_formatted[bill_type][bill_year][bill_month]['usage'][
          bill_account
        ] = usage;
      }
      if (
        bill_account in
        bills_formatted[bill_type][bill_year][bill_month]['cost']
      ) {
        bills_formatted[bill_type][bill_year][bill_month]['cost'][
          bill_account
        ] += bill[cost_key];
      } else {
        bills_formatted[bill_type][bill_year][bill_month]['cost'][
          bill_account
        ] = bill[cost_key];
      }
    });
    setFormattedBills(bills_formatted);
  }, [rawBills, dateOption, consumptionKey, costKey]);

  useEffect(() => {
    let new_keys = [];
    activeIds.forEach((id) => {
      if (id in subAccountInfo) {
        new_keys.push(subAccountInfo[id]['name']);
      }
    });
    setActiveKeys(new_keys);
  }, [activeIds, subAccountInfo]);

  const extractFormattedData = useCallback(
    (
      month_array,
      year_array,
      utilitySelected,
      unitSelected,
      activeKeys,
      type_key,
      formattedBills,
      baseline_month_array,
      baseline_year_array,
      baseline2_month_array,
      baseline2_year_array,
    ) => {
      let factor = 1;
      if (
        UNIT_UTILITIES.includes(utilitySelected) &&
        type_key === 'usage' &&
        Object.keys(ET_FRONTEND_CONVERSIONS).includes(utilitySelected) &&
        Object.keys(ET_FRONTEND_CONVERSIONS[utilitySelected]).includes(
          unitSelected
        )
      ) {
        factor = ET_FRONTEND_CONVERSIONS[utilitySelected][unitSelected];
      }
      return month_array.map((month, idx) => {
        const year = year_array[idx];
        const month_str =
          MONTHSABBREVIATED[month - 1] + '-' + year.toString().substring(2, 4);
        const ret = {
          name: month_str,
          month: month_str,
        };
        if (
          utilitySelected in formattedBills &&
          year in formattedBills[utilitySelected] &&
          month in formattedBills[utilitySelected][year]
        ) {
          Object.keys(
            formattedBills[utilitySelected][year][month][type_key]
          ).forEach((acc) => {
            if (activeKeys.includes(acc)) {
              ret[acc] =
                formattedBills[utilitySelected][year][month][type_key][acc] * factor;
            }
          });
        }
        if (baseline_month_array.length > 0) {
          const baseline_year = baseline_year_array[idx];
          const baseline_month = baseline_month_array[idx]
          if (
            utilitySelected in formattedBills &&
            baseline_year in formattedBills[utilitySelected] &&
            baseline_month in formattedBills[utilitySelected][baseline_year]
          ) {
            Object.keys(
              formattedBills[utilitySelected][baseline_year][baseline_month][type_key]
            ).forEach((acc) => {
              if (activeKeys.includes(acc)) {
                let key = acc + ' Baseline';
                ret[key] =
                  formattedBills[utilitySelected][baseline_year][baseline_month][type_key][
                  acc
                  ] * factor;
              }
            });
          }
        }
        if (baseline2_month_array.length > 0) {
          const baseline2_year = baseline2_year_array[idx];
          const baseline2_month = baseline2_month_array[idx]
          if (
            utilitySelected in formattedBills &&
            baseline2_year in formattedBills[utilitySelected] &&
            baseline2_month in formattedBills[utilitySelected][baseline2_year]
          ) {
            Object.keys(
              formattedBills[utilitySelected][baseline2_year][baseline2_month][type_key]
            ).forEach((acc) => {
              if (activeKeys.includes(acc)) {
                let key = acc + ' Baseline 2';
                ret[key] =
                  formattedBills[utilitySelected][baseline2_year][baseline2_month][type_key][
                  acc
                  ] * factor;
              }
            });
          }
        }
        return ret;
      });
    },
    []
  );

  useEffect(() => {
    if (startDate) {
      let year_array = [];
      let month_array = [];
      let local_date = startDate;

      let local_baseline_date = baselineStartDate;
      let baseline_month_lookup = [];
      let baseline_year_array = [];
      let baseline_month_array = [];

      let local_baseline2_date = baseline2StartDate;
      let baseline2_month_lookup = [];
      let baseline2_year_array = [];
      let baseline2_month_array = [];


      for (let i = 0; i < 12; i++) {
        year_array.push(local_date.year());
        month_array.push(local_date.month() + 1);
        baseline_month_lookup[local_date.month()] = i
        baseline2_month_lookup[local_date.month()] = i
        local_date = local_date.month(local_date.month() + 1);
      }

      if (baselineStartDate) {
        for (let i = 0; i < 12; i++) {
          let idx = baseline_month_lookup[local_baseline_date.month()]
          baseline_year_array[idx] = local_baseline_date.year()
          baseline_month_array[idx] = local_baseline_date.month() + 1
          local_baseline_date = local_baseline_date.month(local_baseline_date.month() + 1);
        }
      }

      if (baseline2StartDate) {
        for (let i = 0; i < 12; i++) {
          let idx2 = baseline2_month_lookup[local_baseline2_date.month()]
          baseline2_year_array[idx2] = local_baseline2_date.year()
          baseline2_month_array[idx2] = local_baseline2_date.month() + 1
          local_baseline2_date = local_baseline2_date.month(local_baseline2_date.month() + 1);
        }
      }

      let usage_data = extractFormattedData(
        month_array,
        year_array,
        utilitySelected,
        unitSelected,
        activeKeys,
        'usage',
        formattedBills,
        baseline_month_array,
        baseline_year_array,
        baseline2_month_array,
        baseline2_year_array,
      );
      let cost_data = extractFormattedData(
        month_array,
        year_array,
        utilitySelected,
        unitSelected,
        activeKeys,
        'cost',
        formattedBills,
        baseline_month_array,
        baseline_year_array,
        baseline2_month_array,
        baseline2_year_array,
      );

      setUsageData(usage_data);
      setCostData(cost_data);
    }
  }, [
    formattedBills,
    utilitySelected,
    unitSelected,
    activeKeys,
    startDate,
    baselineStartDate,
    baseline2StartDate,
    extractFormattedData,
  ]);

  useEffect(() => {
    setBaselineEndDate(null);
    setBaselineStartDate(null);
    setBaseline2EndDate(null);
    setBaseline2StartDate(null);
  }, [startDate]);

  useEffect(() => {
    setBaseline2EndDate(null);
    setBaseline2StartDate(null);
  }, [baselineStartDate]);

  return (
    <>
      {isLoadingBills && (
        <div style={{ marginTop: '300px' }}>
          <Loader />
        </div>
      )}
      {!isLoadingBills && (
        <>
          <Row
            style={{
              display: 'flex',
              float: 'right',
              width: '600px',
              marginRight: '-1em',
              marginTop: '0.5em',
            }}
          >
            <Form.Group as={Col}>
              <Form.Row>
                <Form.Label>Utility:</Form.Label>
                <Form.Control
                  style={styles.inputStyle}
                  as='select'
                  size='sm'
                  onChange={(e) => {
                    setUtilitySelected(e.target.value);
                  }}
                  value={utilitySelected}
                  data-id='building_utility'
                >
                  {activeUtilities.map((utility) => (
                    <option
                      key={`util-tab-${utility.toLowerCase()}`}
                      value={utility.toLowerCase()}
                    >
                      {utility.charAt(0).toUpperCase() +
                        utility.substr(1).toLowerCase()}
                    </option>
                  ))}
                </Form.Control>
              </Form.Row>
            </Form.Group>
            <Col>
              {'Meter Select:  '}
              <FontAwesomeIcon
                style={{
                  fontSize: '22px',
                  marginLeft: '0.5em',
                  color: CHARTCOLORS[1],
                  cursor: 'pointer',
                }}
                icon={faFilterList}
                id={`report-account-select-popover`}
                onClick={() => setShowAccountModal(true)}
              />
            </Col>
          </Row>
          <Row
            style={{
              display: 'flex',
              float: 'right',
              width: '100%',
              marginRight: '-1em',
              marginTop: '0.5em',
            }}
          >
            <Row style={{ width: '100%' }}>
              <Col></Col>
              <Col>
                <DashboardDateSelectContainer
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  dateOption={dateOption}
                  setDateOption={setDateOption}
                  dateOptionChoice={'simple'}
                  isLoading={isLoadingBills}
                />
              </Col>

              <Col>
                <div style={{ display: 'inline-block' }}>
                  {endDate && (
                    <AccountChartingBaselineContainer
                      endDate={endDate}
                      baselineStartDate={baselineStartDate}
                      setBaselineStartDate={setBaselineStartDate}
                      baselineEndDate={baselineEndDate}
                      setBaselineEndDate={setBaselineEndDate}
                      isLoading={isLoadingBills}
                    />
                  )}
                </div>
              </Col>
            </Row>
            {baselineStartDate &&
              <Row style={{ width: '100%' }}>
                <Col></Col>
                <Col></Col>
                <Col>
                  <div style={{ display: 'inline-block' }}>
                    {endDate && (
                      <AccountChartingBaselineContainer
                        endDate={endDate}
                        baselineStartDate={baseline2StartDate}
                        setBaselineStartDate={setBaseline2StartDate}
                        baselineEndDate={baseline2EndDate}
                        setBaselineEndDate={setBaseline2EndDate}
                        isLoading={isLoadingBills}
                        baselineIndex={2}
                      />)}</div>
                </Col>
              </Row>}
          </Row>

          <Row style={{ width: '100%' }}>
            <Col>
              <h5>{'Cost ($)'}   <Form.Group controlId={'cost-type-select'} style={{ display: 'inline-block', marginLeft: '1em' }}>
                <Form.Control
                  as='select'
                  onChange={(e) => setCostKey(e.target.value)}
                  defaultValue={costKey}
                >
                  {Object.keys(costKeys).map((key) => (
                    <option key={`cost-key-opt-${key}`} value={key}>
                      {costKeys[key]['name']}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group><ChartInfoPopover
                  title={'Chart Info'}
                  body={'When viewing individual cost and consumption fields, any account or meter level exclusion rules will be ignored.'}
                /> </h5>
              <div style={{ ...row, marginBottom: '4em' }}>
                <EnergyTracerMultiLineChart
                  data={!costData ? [] : costData}
                  unit={'$'}
                  keys={activeKeys}
                  baseline={baselineStartDate !== null}
                  baseline2={baseline2StartDate !== null}
                  showSummary={costSummary}
                />
                <GraphActionContainer>
                  <ChartToggle
                    onClick={() => {
                      setCostSummary((prev) => !prev);
                    }}
                    state={costSummary ? 1 : 0}
                    title={'Toggle Selection Total'}
                    variant={3}
                  />
                  <ChartDownload
                    organization_id={organizationId}
                    data={costData}
                    headers={chartDownloadHeaders}
                    keys={chartDownloadKeys}
                    chart_type={'line'}
                  />
                </GraphActionContainer>
              </div>

              <h5>
                {`Usage`}
                <Form.Group controlId={'consumption-type-select'} style={{ display: 'inline-block', marginLeft: '1em' }}>
                  <Form.Control
                    as='select'
                    onChange={(e) => setConsumptionKey(e.target.value)}
                    defaultValue={consumptionKey}
                  >
                    {Object.keys(consumptionKeys).map((key) => (
                      <option key={`cons-key-opt-${key}`} value={key}>
                        {consumptionKeys[key]['name']}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
                <ChartInfoPopover
                  title={'Chart Info'}
                  body={'When viewing individual cost and consumption fields, any account or meter level exclusion rules will be ignored.'}
                />
                {UNIT_UTILITIES.includes(utilitySelected) && (
                  <Form.Group style={{ display: 'inline-block', marginLeft: '35px' }}>
                    <Form.Label>
                      Unit:
                    </Form.Label>
                    <Form.Control
                      style={{
                        width: '200px',
                        textAlign: 'center',
                        marginRight: '10px',
                        display: 'inline-block',
                        marginLeft: '5px'
                      }}
                      as='select'
                      size='sm'
                      onChange={(e) => {
                        setUnitSelected(e.target.value);
                      }}
                      value={unitSelected}
                      data-id='db_org_unit'
                    >
                      {consumptionUnits[utilitySelected].map((unit) => (
                        <option key={`unit-tab-${unit}`} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                )}
              </h5>
              <div style={{ ...row, marginBottom: '4em' }}>
                <EnergyTracerMultiLineChart
                  data={!usageData ? [] : usageData}
                  unit={''}
                  keys={activeKeys}
                  baseline={baselineStartDate !== null}
                  baseline2={baseline2StartDate !== null}
                  showSummary={usageSummary}
                />
                <GraphActionContainer>
                  <ChartToggle
                    onClick={() => {
                      setUsageSummary((prev) => !prev);
                    }}
                    state={usageSummary ? 1 : 0}
                    title={'Toggle Selection Total'}
                    variant={3}
                  />
                  <ChartDownload
                    organization_id={organizationId}
                    data={usageData}
                    headers={chartDownloadHeaders}
                    keys={chartDownloadKeys}
                    chart_type={'line'}
                  />
                </GraphActionContainer>
              </div>
            </Col>
          </Row>
          <AccountSelectModal
            show={showAccountModal}
            onHide={() => setShowAccountModal(false)}
            accounts={subAccountInfo}
            accountIds={subAccountIds}
            selectedAccounts={activeIds}
            setSelectedAccounts={setActiveIds}
            setNonSelectedAccounts={() => { }}
          />
        </>
      )}
    </>
  );
}
